/**
 * DocumentIdInput
 * --------------------------------------------------------------
 * Compact toggle between two ingest sources:
 *   (a) Local file upload  (default — backward compatible)
 *   (b) S3 document_id     (only when the system bucket is configured)
 *
 * Usage:
 *   const [src, setSrc] = useState({mode:"file", file:null, documentId:""});
 *   ...
 *   <DocumentIdInput
 *      value={src}
 *      onChange={setSrc}
 *      accept=".pdf,.docx"
 *      testId="cta"
 *   />
 *
 * Submit-time:
 *   if (src.mode === "file") fd.append("file", src.file);
 *   else                     fd.append("document_id", src.documentId);
 *
 * Renders nothing fancy when the system bucket is NOT configured — falls
 * back to a plain file input so existing flows are untouched.
 */
import React, { useEffect, useState } from "react";
import { Upload, Cloud, CheckCircle2, AlertTriangle, Hourglass } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function DocumentIdInput({
    value,
    onChange,
    accept = "",
    testId = "doc-source",
    label = "Document",
    required = true,
    multiple = false,           // protocols may take >1 file
}) {
    const { mode, file, files, documentId } = value || {};
    const [bucketReady, setBucketReady] = useState(null);   // null while loading
    const [bucketLabel, setBucketLabel] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [verifyResult, setVerifyResult] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/admin/connections/system/document-store");
                if (cancelled) return;
                setBucketReady(!!data?.config?.configured);
                setBucketLabel(data?.config?.bucket || "");
            } catch {
                if (!cancelled) setBucketReady(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const setMode = (next) => {
        if (next === mode) return;
        setVerifyResult(null);
        onChange({
            mode: next,
            file: next === "file" ? file : null,
            files: next === "file" ? files : null,
            documentId: next === "document_id" ? documentId : "",
        });
    };

    const verify = async () => {
        if (!documentId?.trim()) {
            toast.error("Enter a document_id (S3 object key) first");
            return;
        }
        setVerifying(true);
        setVerifyResult(null);
        try {
            const { data } = await api.post(
                "/admin/connections/system/document-store/verify-key",
                { document_id: documentId.trim() },
            );
            setVerifyResult(data);
            if (data.ok) toast.success(`Found in bucket — ${data.size_bytes} bytes`);
            else toast.error(data.detail || "Key not found");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Verify failed");
        } finally {
            setVerifying(false);
        }
    };

    // ── Fallback when the bucket isn't configured — show only the
    //    file-upload UI so this component is a drop-in replacement.
    if (bucketReady === false) {
        return (
            <div data-testid={`${testId}-source`}>
                <div className="label-xs mb-1.5">{label}{required ? " *" : ""}</div>
                <input
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    required={required}
                    data-testid={`${testId}-file-input`}
                    onChange={(e) => {
                        const fl = e.target.files;
                        onChange({
                            mode: "file",
                            file: multiple ? null : (fl?.[0] || null),
                            files: multiple ? Array.from(fl || []) : null,
                            documentId: "",
                        });
                    }}
                    className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-[#7C3AED] file:text-white file:cursor-pointer"
                />
            </div>
        );
    }

    return (
        <div data-testid={`${testId}-source`}>
            <div className="label-xs mb-1.5">{label}{required ? " *" : ""}</div>

            {/* Mode pills */}
            <div className="inline-flex rounded-md border border-white/15 overflow-hidden mb-2" role="tablist">
                <button type="button" onClick={() => setMode("file")}
                    data-testid={`${testId}-mode-file`}
                    className={`px-2.5 py-1 text-[11px] inline-flex items-center gap-1.5 ${
                        mode === "file"
                            ? "bg-white text-black"
                            : "bg-transparent text-white/60 hover:bg-white/5"
                    }`}>
                    <Upload className="w-3 h-3" /> Upload file
                </button>
                <button type="button" onClick={() => setMode("document_id")}
                    data-testid={`${testId}-mode-document-id`}
                    disabled={bucketReady === null}
                    title={bucketReady ? `From bucket ${bucketLabel}` : "Loading…"}
                    className={`px-2.5 py-1 text-[11px] inline-flex items-center gap-1.5 border-l border-white/15 ${
                        mode === "document_id"
                            ? "bg-white text-black"
                            : "bg-transparent text-white/60 hover:bg-white/5"
                    }`}>
                    <Cloud className="w-3 h-3" /> From S3 document_id
                </button>
            </div>

            {mode === "document_id" ? (
                <div className="space-y-2">
                    <div className="flex items-stretch gap-2">
                        <input
                            type="text"
                            value={documentId || ""}
                            onChange={(e) => {
                                setVerifyResult(null);
                                onChange({ mode: "document_id", file: null, files: null, documentId: e.target.value });
                            }}
                            placeholder="protocols/2026/acme-301.pdf"
                            required={required}
                            data-testid={`${testId}-document-id-input`}
                            className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-xs font-mono"
                        />
                        <button type="button" onClick={verify} disabled={verifying || !documentId?.trim()}
                            data-testid={`${testId}-verify-btn`}
                            className="px-3 py-1.5 text-[11px] border border-violet-300/30 text-violet-200 rounded-md hover:bg-violet-300/10 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
                            {verifying ? <Hourglass className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                            Verify
                        </button>
                    </div>
                    <div className="text-[10px] text-white/40">
                        S3 object key in <code className="text-white/65">{bucketLabel || "(unconfigured)"}</code>.
                        Bytes are fetched from S3 and processed through the same dedup/AI pipeline as a multipart upload.
                    </div>
                    {verifyResult && (
                        <div className={`px-2.5 py-1.5 rounded-md text-[11px] flex items-start gap-1.5 ${
                            verifyResult.ok
                                ? "border border-emerald-300/30 bg-emerald-300/5 text-emerald-100"
                                : "border border-rose-300/30 bg-rose-300/5 text-rose-100"
                        }`} data-testid={`${testId}-verify-result`}>
                            {verifyResult.ok
                                ? <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                : <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />}
                            <span className="font-mono break-all">{verifyResult.detail}</span>
                        </div>
                    )}
                </div>
            ) : (
                <input
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    required={required}
                    data-testid={`${testId}-file-input`}
                    onChange={(e) => {
                        const fl = e.target.files;
                        onChange({
                            mode: "file",
                            file: multiple ? null : (fl?.[0] || null),
                            files: multiple ? Array.from(fl || []) : null,
                            documentId: "",
                        });
                    }}
                    className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-[#7C3AED] file:text-white file:cursor-pointer"
                />
            )}
        </div>
    );
}

/** Convenience: builds the empty initial state for the input. */
export const emptyDocSource = (mode = "file") => ({
    mode,
    file: null,
    files: null,
    documentId: "",
});
